// FEFE failsafe, AI board and operator notes helpers shared by the map
// popup, device list and Overview. All read GET /devices fields only.

export const FEFE_SEGMENT_ID = 0xFEFE; // 65278: AP accepts children from any segment
export const LEGACY_WILDCARD_SEGMENT_ID = 0xFFFF; // 65535

function isJnet2(device) {
  const compatible = device?.compatible || device?.capabilities?.compatible;
  return String(compatible || '').toLowerCase() === 'jnet2';
}

export function deviceSegmentApIds(device) {
  const ids = device?.segment?.segment_id_ap;
  return Array.isArray(ids) ? ids.map(Number).filter(Number.isFinite) : [];
}

// The FEFE toggle is offered on jnet2 devices whose state report carries a
// segment block (GET /devices `segment`).
export function deviceSupportsApFailsafe(device) {
  return isJnet2(device) && Boolean(device?.segment) && typeof device.segment === 'object';
}

export function deviceHasApFailsafe(device) {
  return deviceSegmentApIds(device).includes(FEFE_SEGMENT_ID);
}

function isCapModel(device) {
  const model = device?.capabilities?.model || device?.model || '';
  return String(model).toLowerCase().includes('cap');
}

// treeIdForApRadio maps a private-WiFi radio to its treemgmt tree.
// CAP state reports use wifi1/wifi2; treemgmt uses 5GHz_0/5GHz_1.
// apIndex is the AP card's position among private AP radios (0, 1, …)
// and is used when the reported radio name is not one of those.
export function treeIdForApRadio(device, radioName, apIndex) {
  const name = String(radioName || '').trim().toLowerCase();
  if (isCapModel(device)) {
    if (name === 'wifi1' || name === '5ghz_0' || name === 'ath1') return '5GHz_0';
    if (name === 'wifi2' || name === '5ghz_1' || name === 'ath2') return '5GHz_1';
    if (apIndex === 0) return '5GHz_0';
    if (apIndex === 1) return '5GHz_1';
    return '';
  }
  const match = name.match(/^([56])ghz_(\d+)$/);
  if (match) return `${match[1]}GHz_${match[2]}`;
  const lists = Array.isArray(device?.ap_segments) ? device.ap_segments : [];
  if (lists.length === 1 && lists[0]?.tree_id) return lists[0].tree_id;
  return '5GHz_0';
}

// apSegmentIdsForTree is that radio's segment_id_ap. When the device has no
// per-radio lists yet, the shared runtime segment is used.
export function apSegmentIdsForTree(device, treeId) {
  const lists = Array.isArray(device?.ap_segments) ? device.ap_segments : [];
  if (lists.length === 0) return deviceSegmentApIds(device);
  const want = String(treeId || '').toLowerCase();
  const row = lists.find((item) => String(item?.tree_id || '').toLowerCase() === want);
  if (!row || !Array.isArray(row.segment_id_ap)) return [];
  return row.segment_id_ap.map(Number).filter(Number.isFinite);
}

// Radios the map popup can target. Uses reported AP rows when present,
// otherwise one row for a node and both CAP backhaul radios.
export function apFailsafeRadioChoices(device) {
  const lists = Array.isArray(device?.ap_segments) ? device.ap_segments : [];
  const treeIds = lists.length > 0
    ? lists.map((row) => row.tree_id).filter(Boolean)
    : (isCapModel(device) ? ['5GHz_0', '5GHz_1'] : ['5GHz_0']);
  return treeIds.map((treeId) => ({
    treeId,
    enabled: apSegmentIdsForTree(device, treeId).includes(FEFE_SEGMENT_ID),
  }));
}

export function segmentIdLabel(id) {
  const n = Number(id);
  if (n === FEFE_SEGMENT_ID) return 'FEFE';
  if (n === LEGACY_WILDCARD_SEGMENT_ID) return 'FFFF';
  return String(n);
}

// parseSegmentIdToken accepts a decimal id, or the FEFE / FFFF wildcards
// written as hex or as those four letters. Returns undefined when invalid.
export function parseSegmentIdToken(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return undefined;
  const upper = text.toUpperCase();
  if (upper === 'FEFE' || upper === '0XFEFE') return FEFE_SEGMENT_ID;
  if (upper === 'FFFF' || upper === '0XFFFF') return LEGACY_WILDCARD_SEGMENT_ID;
  if (!/^\d+$/.test(text)) return undefined;
  const n = Number(text);
  if (!Number.isInteger(n) || n < 0 || n > 0xFFFF) return undefined;
  return n;
}

export function sameSegmentIdList(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  if (left.length !== right.length) return false;
  return left.every((id, index) => Number(id) === Number(right[index]));
}

export function deviceAiBoardMac(device) {
  if (!isJnet2(device)) return '';
  const mac = device?.ai_device_mac
    || device?.ai_mac_address
    || device?.provisioning?.ai_device_mac
    || device?.provisioning?.AiDeviceMac;
  return String(mac || '').trim();
}

export function deviceHasAiBoard(device) {
  return deviceAiBoardMac(device) !== '';
}

export function deviceNotes(device) {
  return String(device?.notes || '').trim();
}

export function deviceHasNotes(device) {
  return deviceNotes(device) !== '';
}

export function devicesWithNotes(devices) {
  return (Array.isArray(devices) ? devices : []).filter(deviceHasNotes);
}

export function truncateNote(text, max = 80) {
  const flat = String(text || '').replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

// Serials of devices whose model and firmware match a Versions row in the
// Overview (same keys GET /project/network-status aggregates on).
export function serialsForFirmwareVersion(devices, deviceType, version) {
  const wantType = String(deviceType || '');
  const wantVersion = String(version || '');
  return (Array.isArray(devices) ? devices : [])
    .filter((d) => {
      const model = d?.capabilities?.model || 'unknown';
      const firmware = d?.firmware || 'unknown';
      return model === wantType && firmware === wantVersion;
    })
    .map((d) => d.serial_number)
    .filter(Boolean);
}
